import { ISupportingActor } from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { useTheme } from '@emotion/react';
import { flatten } from 'lodash';
import { useMemo } from 'react';
import {
  Color,
  normalizeColor
} from '../../../../../../../components/ColoredChip';
import { TokenDefinition } from '../../../../../../../components/MarkdownText';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../../i18next';
import { getAllUserInterpolationFields } from '../../../../../../../services/set/interpolation';
import { isUserFieldVisible } from '../../../../../../../services/user';
import {
  ActorDescriptor,
  useActorDescriptorsFromTemplate,
  usePartcipantActorDescriptor
} from '../../../../Actors';

const actorDescriptorToTokens = (
  a: ActorDescriptor,
  customFields: CustomUserField[],
  t: UnsafeTFunction
): TokenDefinition[] => {
  const fields = getAllUserInterpolationFields(customFields, t);
  return fields.map((f) => ({
    group: a.label,
    label: f.label,
    token: `${a.key}.${f.value}`,
    color: (a.color as unknown) as Color, // TODO - make actor colors fixed, using the chip shades maybe?
    type: f.type
  }));
};

export const useTokenDefinitionsForExperience = (
  supportingActors: { [actorKey: string]: ISupportingActor },
  customFields: CustomUserField[],
  context: {
    orgId: string | null;
    experienceTemplateId: string | null;
  }
) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    supportingActors,
    false
  );
  const { orgId, experienceTemplateId } = context;
  return useMemo<TokenDefinition[]>(() => {
    return [
      {
        label: 'Title',
        group: 'Experience',
        token: 'experience.title',
        color: 'black',
        type: 'string'
      },
      {
        label: 'Name',
        group: 'Org',
        token: 'org.name',
        color: 'purple',
        type: 'string'
      },
      ...flatten(
        Object.values(actorDescriptors).map((ac) =>
          actorDescriptorToTokens(ac, customFields, t).filter((f) =>
            isUserFieldVisible({ orgId, experienceTemplateId }, f.label)
          )
        )
      ).map((tk) => ({
        ...tk,
        color: normalizeColor(tk.color, theme) as Color
      }))
    ];
  }, [actorDescriptors, customFields, t, theme, orgId, experienceTemplateId]);
};

export const useEmailTokens = (tokens: TokenDefinition[]) => {
  return useMemo(() => {
    return tokens.filter((tk) => {
      // Might lose a couple custom fields here
      return (
        tk.type === 'string' &&
        (tk.token.endsWith('email') || tk.token.endsWith('Email'))
      );
    });
  }, [tokens]);
};

export const useNameTokens = (tokens: TokenDefinition[]) => {
  return useMemo(() => {
    return tokens.filter((tk) => {
      return tk.type === 'string' && tk.token.endsWith('Name');
    });
  }, [tokens]);
};

export const useTokenDefinitionsForMoment = (
  orgId: string,
  customFields: CustomUserField[]
) => {
  const { t } = useUnsafeTranslation();
  const descriptor = usePartcipantActorDescriptor();
  return useMemo<TokenDefinition[]>(() => {
    const descriptorTokens = actorDescriptorToTokens(
      descriptor,
      customFields,
      t
    );
    return [
      {
        label: 'Name',
        group: 'Org',
        token: 'org.name',
        color: 'purple',
        type: 'string'
      },
      ...descriptorTokens.filter((x) => {
        return isUserFieldVisible(
          { orgId, experienceTemplateId: 'MOMENT' },
          x.label
        );
      })
    ];
  }, [descriptor, customFields, t, orgId]);
};
